'use client';

import { Search, Tag } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { useCallback, useEffect, useState } from 'react';

import type { Suggestion } from '@website/services/suggestions.service';

/** Queries below this length are ignored by the search API (unindexable). */
export const MIN_KEYWORD_QUERY_LENGTH = 3;

/** Matches the API's accepted query length (getActivities / getSuggestions). */
export const MAX_KEYWORD_QUERY_LENGTH = 80;

export interface SuggestionItem {
  key: string;
  name: string;
  type: 'query' | 'category' | 'activity';
  /** Backing entity id for category/activity suggestions (activity picks deep-link to the activity page). */
  id?: string;
}

/**
 * Items shown in the suggestions listbox: the raw query first (only once it
 * is long enough to actually search), then the fetched suggestions minus
 * exact duplicates of the query. Exported so the inputs driving the listbox
 * run keyboard navigation over the same list the user sees.
 */
export function buildSuggestionItems(query: string, suggestions: Suggestion[]): SuggestionItem[] {
  const trimmed = query.trim();
  if (!trimmed) return [];
  const rest = suggestions
    .filter((s) => s.name.toLowerCase() !== trimmed.toLowerCase())
    .map((s) => ({ key: `${s.type}:${s.id}`, name: s.name, type: s.type, id: s.id }));
  if (trimmed.length < MIN_KEYWORD_QUERY_LENGTH) return rest;
  return [{ key: 'query', name: trimmed, type: 'query' as const }, ...rest];
}

export function suggestionOptionId(listboxId: string, index: number): string {
  return `${listboxId}-option-${index}`;
}

/**
 * Keyboard navigation for a suggestions listbox attached to a text input
 * (combobox pattern): ArrowUp/Down move the highlight, Enter picks the
 * highlighted item (or submits the raw text when nothing is highlighted),
 * Escape closes. The highlight resets when the dropdown closes, the list
 * changes size, or `resetKey` (typically the input value) changes — the last
 * one matters because `placeholderData` keeps the previous suggestions while
 * a refetch is in flight, so the list length alone can stay stable across
 * edits.
 */
export function useSuggestionNavigation({
  items,
  isOpen,
  open,
  close,
  onPick,
  onSubmit,
  resetKey
}: {
  items: SuggestionItem[];
  isOpen: boolean;
  open: () => void;
  close: () => void;
  onPick: (item: SuggestionItem) => void;
  onSubmit: () => void;
  resetKey: string;
}) {
  const [highlightedIndex, setHighlightedIndex] = useState(-1);

  useEffect(() => {
    setHighlightedIndex(-1);
  }, [items.length, resetKey]);

  useEffect(() => {
    if (!isOpen) setHighlightedIndex(-1);
  }, [isOpen]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      // Don't hijack keys while an IME composition is being confirmed.
      if (e.nativeEvent.isComposing) return;
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        if (!isOpen) open();
        setHighlightedIndex((i) => Math.min(i + 1, items.length - 1));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setHighlightedIndex((i) => Math.max(i - 1, -1));
      } else if (e.key === 'Escape') {
        close();
      } else if (e.key === 'Enter') {
        const highlighted = isOpen && highlightedIndex >= 0 ? items[highlightedIndex] : undefined;
        if (highlighted) {
          onPick(highlighted);
        } else {
          onSubmit();
        }
      }
    },
    [items, isOpen, open, close, onPick, onSubmit, highlightedIndex]
  );

  return { highlightedIndex, handleKeyDown };
}

export interface KeywordSuggestionsListProps {
  listboxId: string;
  items: SuggestionItem[];
  highlightedIndex: number;
  isLoading: boolean;
  onSelect: (item: SuggestionItem) => void;
  /**
   * Shown when the typed query is too short to search (1-2 chars): the API
   * ignores such keywords, so instead of silently doing nothing the dropdown
   * tells the user to keep typing.
   */
  showMinLengthHint?: boolean;
}

/**
 * Inner content for the keyword suggestions panel.
 * No positioning — consumers wrap this in either an absolute container or a Popover.
 */
export function KeywordSuggestionsList({
  listboxId,
  items,
  highlightedIndex,
  isLoading,
  onSelect,
  showMinLengthHint = false
}: KeywordSuggestionsListProps) {
  const t = useTranslations('SearchPage');

  if (items.length === 0 && !showMinLengthHint && !isLoading) {
    return null;
  }

  // The listbox container always renders while the dropdown is open so the
  // input's aria-controls/aria-activedescendant never point at a missing id.
  // Status rows (hint / searching) live OUTSIDE the listbox: a listbox may
  // only own option/group children.
  return (
    <>
      <div role="listbox" id={listboxId} aria-label={t('searchPlaceholder')}>
        {items.map((item, index) => {
          const Icon = item.type === 'category' ? Tag : Search;
          return (
            <button
              key={item.key}
              id={suggestionOptionId(listboxId, index)}
              role="option"
              aria-selected={index === highlightedIndex}
              type="button"
              // Combobox pattern: focus stays in the input; options are
              // reached via ArrowUp/Down (aria-activedescendant), not Tab.
              tabIndex={-1}
              className={`w-full flex items-center gap-2 px-4 py-2.5 transition-colors text-left ${
                index === highlightedIndex ? 'bg-neutral-100' : 'hover:bg-neutral-50'
              }`}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => onSelect(item)}
            >
              <Icon className="w-4 h-4 text-neutral-400 shrink-0" />
              <span className="text-sm font-medium text-neutral-800 truncate">{item.name}</span>
            </button>
          );
        })}
      </div>
      {showMinLengthHint && (
        <div className="px-4 py-2.5 text-sm text-neutral-500" role="status">
          {t('keywordMinLength', { min: MIN_KEYWORD_QUERY_LENGTH })}
        </div>
      )}
      {!showMinLengthHint && isLoading && items.length <= 1 && (
        <div className="px-4 py-2.5 text-sm text-neutral-500" role="status">
          {t('searching')}
        </div>
      )}
    </>
  );
}

/**
 * Absolutely-positioned dropdown panel wrapping KeywordSuggestionsList.
 * Used in HomeSearchBar where the parent has no overflow clipping.
 */
export function KeywordSuggestionsDropdown({
  className,
  ...listProps
}: KeywordSuggestionsListProps & { className?: string }) {
  return (
    <div
      className={`absolute top-full left-0 right-0 mt-1 bg-white border border-neutral-200 rounded-lg shadow-lg z-50 max-h-[280px] overflow-y-auto ${className || ''}`}
    >
      <KeywordSuggestionsList {...listProps} />
    </div>
  );
}
